import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { Upload, AlertTriangle, RefreshCw } from 'lucide-react';
import { getThemeURL } from '../../../../Services/EndPoints';
import { getData } from '../../../../Services/Services';

const MapEdit = ({
  newItem,
  setNewItem,
  savedQueries,
  dataSourcesForMap,
  setDataSourcesForMap,
  uploadedIcons,
  setUploadedIcons,
  iconSizes,
  setIconSizes,
  iconMap,
  setLoader,
  labelStyle,
  handleIconUpload,
  attachmentList,
  setAttachmentList,
  handleDateFieldsUpdate,
  selectedBankIds
}) => {

  console.log("MapEdit attachmentList", attachmentList);
  const [noDataForDateRange, setNoDataForDateRange] = useState(false);
  // Local state to manage widget-specific icons
  const [widgetIcons, setWidgetIcons] = useState({});
  const [blobUrls, setBlobUrls] = useState(new Set());

  // Helper function to determine filter fields dynamically
  const determineFilterFields = (queryObject) => {
    // Extract table name from entity
    const entity = queryObject.entity || "";
    const tableName = entity.includes(".") ? entity.split(".")[1] : entity;
    
    // Get joins information
    const joins = queryObject.joins || [];
    const joinedTables = joins.map(join => {
      const table = join.table || join.alias || "";
      return table.includes(".") ? table.split(".")[1] : table;
    });

    // Helper function to determine filter field names
    let bankFilterField = null;

    // Check what tables are available
    const hasBankTable = tableName === "bank" || joinedTables.includes("bank");
    const hasJoins = joins.length > 0;

    console.log("MapEdit: Table analysis:", {
      tableName,
      joinedTables,
      hasBankTable,
      hasJoins
    });

    // Determine bank filter field
    if (tableName === "bank") {
      // If entity is bank table
      bankFilterField = "bank.id";
    } else if (hasBankTable && hasJoins) {
      // If joins are there and bank table is available
      bankFilterField = "bank.id";
    } else if (!hasBankTable && tableName !== "bank" && tableName !== "branch_details") {
      // For core tables (not bank or branch_details), use table.bank_id
      bankFilterField = `${tableName}.bank_id`;
    } else if (!hasBankTable) {
      // If no bank table available, use current table with bank_id
      bankFilterField = `${tableName}.bank_id`;
    }

    console.log("MapEdit: Determined bank filter field:", bankFilterField);

    return { bankFilterField };
  };

  // Get unique widget identifier
  const getWidgetId = () => {
    return newItem?.widgetId || newItem?.id;
  };

  // Create widget-specific key for icons
  const getWidgetIconKey = (serviceName) => {
    const widgetId = getWidgetId();
    return widgetId ? `widget_${widgetId}_service_${serviceName}` : null;
  };

  // Initialize widget icons from attachments
  useEffect(() => {
    const widgetId = getWidgetId();
    if (!widgetId) return;

    console.log(`=== Initializing icons for widget ${widgetId} ===`);
    
    const newWidgetIcons = {};
    const newBlobUrls = new Set();

    // Clean up old blob URLs
    blobUrls.forEach(url => {
      if (url && typeof url === 'string' && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });

    if (attachmentList && attachmentList.length > 0) {
      attachmentList.forEach(attachment => {
        if (attachment.type === "MAP" && 
            attachment.widgetId === widgetId && 
            attachment.serviceName &&
            !attachment._markedForDeletion) {
          
          const iconKey = getWidgetIconKey(attachment.serviceName);
          if (!iconKey) return;

          console.log(`Processing attachment for service: ${attachment.serviceName}`);
          
          // Priority: mediaPath > file array
          if (attachment.mediaPath && typeof attachment.mediaPath === 'string') {
            newWidgetIcons[iconKey] = attachment.mediaPath;
            console.log(`✓ Set mediaPath for ${iconKey}`);
          } else if (attachment.file && Array.isArray(attachment.file) && attachment.file.length > 0) {
            try {
              const uint8Array = new Uint8Array(attachment.file);
              const blob = new Blob([uint8Array], { 
                type: attachment.mimeType || 'image/png' 
              });
              const blobUrl = URL.createObjectURL(blob);
              newWidgetIcons[iconKey] = blobUrl;
              newBlobUrls.add(blobUrl);
              console.log(`✓ Created blob URL for ${iconKey}`);
            } catch (error) {
              console.error('Error creating blob URL:', error);
            }
          }
        }
      });
    }

    setWidgetIcons(newWidgetIcons);
    setBlobUrls(newBlobUrls);
    
    console.log(`Final widget icons:`, newWidgetIcons);
  }, [newItem.widgetId, newItem.id, attachmentList]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      blobUrls.forEach(url => {
        if (url && typeof url === 'string' && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  const getDataSourceAsArray = () => {
    if (!newItem.dataSource) return [];
    return Array.isArray(newItem.dataSource) ? newItem.dataSource : [newItem.dataSource];
  };

  const getFieldOrderOptions = (sourceKey) => {
    const fields = newItem.fieldsBySource?.[sourceKey] || [];
    const visibleSelectedFields = fields.filter((f) => f.selected !== false && !f.hidden);
    return visibleSelectedFields.map((_, i) => ({ value: i + 1, label: i + 1 }));
  };

  // Add this function inside MapEdit component
  const getDateFields = () => {
    const sources = getDataSourceAsArray();
    let dateFields = [];
    
    sources.forEach((source) => {
      // Use original data, not filtered data
      const sampleData = dataSourcesForMap[source]?.originalData || dataSourcesForMap[source]?.sampleData || [];
      if (sampleData.length === 0) return;
      
      const fields = new Map();
      Object.keys(sampleData[0]).forEach(key => {
        const value = sampleData[0][key];
        
        // Check if the value looks like a date or timestamp
        if (typeof value === 'string' && value !== null && value !== '') {
          const datePatterns = [
            /^\d{4}-\d{2}-\d{2}$/,                    // YYYY-MM-DD (date)
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/,  // ISO timestamp
            /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/  // YYYY-MM-DD HH:MM:SS
          ];
          
          const isDateField = datePatterns.some(pattern => pattern.test(value));
          
          if (isDateField) {
            // Use the full column name from your response
            const fullColumnName = `atm_details.${key}`;
            
            fields.set(key, {
              key: fullColumnName,
              label: key.replace(/([A-Z_])/g, " $1").replace(/^./, (str) => str.toUpperCase()),
              type: value.includes('T') || value.includes(' ') ? 'timestamp' : 'date'
            });
          }
        }
      });
      
      dateFields = dateFields.concat(Array.from(fields.values()));
    });
    
    return dateFields;
  };
  
  // Get available date fields
  const dateFields = getDateFields();

  const handleMapDateRefresh = async () => {
    if (!newItem.dataSource || !newItem.fromDate || !newItem.toDate || !newItem.selectedDateField) {
      return;
    }

    setLoader(true);

    const sources = getDataSourceAsArray().filter(
      (source) => savedQueries.some((q) => q.serviceName === source)
    );

    for (const source of sources) {
      const matchedQuery = savedQueries.find(
        (q) => q.serviceName === source
      );
      if (!matchedQuery) continue;

      const queryObject = typeof matchedQuery.queryBuilder === "string"
        ? JSON.parse(matchedQuery.queryBuilder)
        : matchedQuery.queryBuilder;

      const filters = {};

      try {
        const userResponse = await getData(getThemeURL);
        if (
          userResponse &&
          Array.isArray(userResponse) &&
          userResponse.length > 0
        ) {
          const user = userResponse[0];
          
          // **UPDATED**: Apply dynamic filter logic for date filter refresh
          if (user.role_name === "SUPER ADMIN") {
            // For Super Admin, use selectedBankIds if available
            if (selectedBankIds && selectedBankIds.length > 0) {
              // **NEW**: Use dynamic filter field determination
              const { bankFilterField } = determineFilterFields(queryObject);
              
              if (bankFilterField) {
                filters[bankFilterField] = {
                  operator: "IN",
                  value: selectedBankIds.map(Number)
                };
                console.log("Applied dynamic bank filter for Super Admin in date filter:", filters[bankFilterField]);
              }
            }
          } else {
            // For non-Super Admin users, use their specific IDs with dynamic field determination
            const { bankFilterField } = determineFilterFields(queryObject);
            
            if (user.bank_id && bankFilterField) {
              filters[bankFilterField] = {
                operator: "IN",
                value: [user.bank_id] // Convert single ID to array format
              };
              console.log("Applied dynamic bank filter for regular user in date filter:", filters[bankFilterField]);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching user profile for filters:", error);
      }

      // Create date filter with timestamp handling
      const dateRangeFilter = {};

      // Check if the selected field is a timestamp type
      const selectedFieldData = dateFields.find(field => field.key === newItem.selectedDateField);
      const isTimestamp = selectedFieldData && selectedFieldData.type === 'timestamp';
      handleDateFieldsUpdate(dateFields);
      
      if (isTimestamp) {
        // Append timestamp to fromDate and toDate for timestamp fields with space
        const fromDateTime = newItem.fromDate + ' 00:00:00.000';
        const toDateTime = newItem.toDate + ' 23:59:59.999'; // Use end of day for toDate
        
        dateRangeFilter[newItem.selectedDateField] = {
          "operator": "BETWEEN",
          "value": [fromDateTime, toDateTime]
        };
      } else {
        // Use date values as-is for date-only fields
        dateRangeFilter[newItem.selectedDateField] = {
          "operator": "BETWEEN", 
          "value": [newItem.fromDate, newItem.toDate]
        };
      }

      const enhancedQuery = {
        ...queryObject,
        filters: {
          ...queryObject.filters,
          ...dateRangeFilter,
          ...filters
        }
      };

      console.log("Map query with date filter:", enhancedQuery);

      try {
        const response = await fetch("/nifi/fetchQuery", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(enhancedQuery)
        });
        
        if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
        const result = await response.json();

        if(result.data.length === 0) {
          setNoDataForDateRange(true);
          setLoader(false);
          return;
        } else {
          setNoDataForDateRange(false);
        }

        console.log("Filtered map data:", result);

        // Filter out entries with null/empty latitude/longitude
        const validData = (result.data || []).filter(item => 
          item.latitude && 
          item.longitude && 
          item.latitude !== "" && 
          item.longitude !== "" &&
          item.latitude !== null &&
          item.longitude !== null
        );

        const cleanColumns = result.columns?.map(col =>
          col.includes(".") ? col.split(".").pop() : col
        ) || [];

        const allFields = cleanColumns.map((field, index) => ({
          key: field,
          label: field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()),
          selected: true,
          order: index + 1,
          hidden: field === "bank_id" || field === "branch_id",
        }));

        setDataSourcesForMap(prev => ({
          ...prev,
          [source]: {
            sampleData: validData, // Filtered data for display
            originalData: prev[source]?.originalData || validData, // Keep original data
            columns: cleanColumns,
          },
        }));

        setNewItem(prev => ({
          ...prev,
          fieldsBySource: {
            ...prev.fieldsBySource,
            [source]: allFields
          },
          fromDate: prev.fromDate,
          toDate: prev.toDate,
          selectedDateField: prev.selectedDateField, // This should now persist
          dateFilterApplied: true,
          dateFieldsChanged: false,
        }));

      } catch (err) {
        console.error("Failed to fetch data for map (with date filter):", err);
        setLoader(false); // ✅ Also ensure loader is stopped on error
        return;
      }
    }

    setLoader(false);
  };

  const getDefaultIcon = () => {
    return iconMap.default?.options?.iconUrl || 
           "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJMMTMuMDkgOC4yNkwyMCA5TDEzLjA5IDE1Ljc0TDEyIDIyTDEwLjkxIDE1Ljc0TDQgOUwxMC45MSA4LjI2TDEyIDJaIiBmaWxsPSIjM0I4MkY2Ii8+PC9zdmc+";
  };

  const getIconSource = (serviceName) => {
    const iconKey = getWidgetIconKey(serviceName);
    
    console.log(`Getting icon for service: ${serviceName}, key: ${iconKey}`);
    
    // Priority 1: Widget-specific icons (from attachments or uploads)
    if (iconKey && widgetIcons[iconKey]) {
      console.log(`✓ Found widget-specific icon`);
      return widgetIcons[iconKey];
    }
    
    // Priority 2: Global uploaded icons (current session)
    if (iconKey && uploadedIcons && uploadedIcons[iconKey]) {
      console.log(`✓ Found in uploaded icons`);
      return uploadedIcons[iconKey];
    }
    
    // Priority 3: Icon map fallback
    if (iconMap && iconMap[serviceName] && iconMap[serviceName].options && iconMap[serviceName].options.iconUrl) {
      console.log(`✓ Using iconMap`);
      return iconMap[serviceName].options.iconUrl;
    }
    
    // Priority 4: Default icon
    console.log(`✓ Using default icon`);
    return getDefaultIcon();
  };

  const hasCustomIcon = (serviceName) => {
    const iconKey = getWidgetIconKey(serviceName);
    const widgetId = getWidgetId();
    
    // Check widget-specific icons
    if (iconKey && widgetIcons[iconKey]) return true;
    
    // Check uploaded icons
    if (iconKey && uploadedIcons && uploadedIcons[iconKey]) return true;
    
    // Check attachments
    if (attachmentList && widgetId) {
      return attachmentList.some(attachment => 
        attachment.type === "MAP" && 
        attachment.widgetId === widgetId && 
        attachment.serviceName === serviceName &&
        !attachment._markedForDeletion &&
        (attachment.file || attachment.mediaPath)
      );
    }
    
    return false;
  };

  const getCurrentUserId = () => {
    return newItem.created_by || 268;
  };

  const handleIconUploadWithAttachment = async (serviceName, file) => {
    const widgetId = getWidgetId();
    if (!widgetId) {
      console.error('No widget ID available');
      return;
    }

    try {
      const iconKey = getWidgetIconKey(serviceName);
      if (!iconKey) return;

      console.log(`=== Uploading icon for widget ${widgetId}, service ${serviceName} ===`);

      // Clean up existing blob URL for this icon
      const existingUrl = widgetIcons[iconKey];
      if (existingUrl && typeof existingUrl === 'string' && existingUrl.startsWith('blob:')) {
        URL.revokeObjectURL(existingUrl);
        setBlobUrls(prev => {
          const newSet = new Set(prev);
          newSet.delete(existingUrl);
          return newSet;
        });
      }

      // Create new blob URL and update widget icons immediately
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        setWidgetIcons(prev => ({
          ...prev,
          [iconKey]: dataUrl
        }));
        
        // Also update global uploadedIcons for consistency
        setUploadedIcons(prev => ({
          ...prev,
          [iconKey]: dataUrl
        }));
        
        console.log(`✓ Set icon for key: ${iconKey}`);
      };
      reader.readAsDataURL(file);

      // Process file for attachment storage
      const arrayBufferReader = new FileReader();
      arrayBufferReader.onload = (e) => {
        const arrayBuffer = e.target.result;
        const uint8Array = new Uint8Array(arrayBuffer);
        const byteArray = Array.from(uint8Array);

        // Find existing attachment for this widget and service
        const existingIndex = attachmentList ? attachmentList.findIndex(attachment => 
          attachment.type === "MAP" && 
          attachment.widgetId === widgetId && 
          attachment.serviceName === serviceName
        ) : -1;

        const newAttachment = {
          type: "MAP",
          created_by: getCurrentUserId(),
          widgetName: newItem.title || 'New Widget',
          widgetId: widgetId,
          fileName: file.name,
          serviceName: serviceName,
          file: byteArray,
          mimeType: file.type,
          created_on: Date.now(),
          dashboardId: newItem.dashboardId || null,
          id: existingIndex !== -1 ? attachmentList[existingIndex].id : null,
          mediaPath: null,
          fileSize: file.size
        };

        console.log(`✓ Creating attachment:`, newAttachment);

        setAttachmentList(prevAttachments => {
          const updatedAttachments = [...(prevAttachments || [])];
          
          if (existingIndex !== -1) {
            updatedAttachments[existingIndex] = newAttachment;
          } else {
            updatedAttachments.push(newAttachment);
          }
          
          return updatedAttachments;
        });
      };
      
      arrayBufferReader.readAsArrayBuffer(file);

      // Call external handler if provided
      if (handleIconUpload && typeof handleIconUpload === 'function') {
        await handleIconUpload(serviceName, file);
      }

    } catch (error) {
      console.error('Error handling icon upload:', error);
    }
  };

  const handleResetIcon = (serviceName) => {
    const widgetId = getWidgetId();
    const iconKey = getWidgetIconKey(serviceName);
    
    if (!widgetId || !iconKey) return;

    console.log(`=== Resetting icon for widget ${widgetId}, service ${serviceName} ===`);

    // Clean up blob URL if exists
    const existingUrl = widgetIcons[iconKey];
    if (existingUrl && typeof existingUrl === 'string' && existingUrl.startsWith('blob:')) {
      URL.revokeObjectURL(existingUrl);
      setBlobUrls(prev => {
        const newSet = new Set(prev);
        newSet.delete(existingUrl);
        return newSet;
      });
    }

    // Remove from widget icons
    setWidgetIcons(prev => {
      const newIcons = { ...prev };
      delete newIcons[iconKey];
      return newIcons;
    });

    // Remove from global uploaded icons
    setUploadedIcons(prev => {
      const newIcons = { ...prev };
      delete newIcons[iconKey];
      return newIcons;
    });

    // Mark attachment for deletion
    setAttachmentList(prevAttachments => {
      if (!prevAttachments) return prevAttachments;
      
      return prevAttachments.map(attachment => {
        if (attachment.type === "MAP" && 
            attachment.widgetId === widgetId && 
            attachment.serviceName === serviceName &&
            !attachment._markedForDeletion) {
          
          if (attachment.id) {
            return { ...attachment, _markedForDeletion: true };
          }
          return null; // Remove if no ID (new attachment)
        }
        return attachment;
      }).filter(Boolean);
    });
  };
 
  return (
    <>
      <div style={{ display: "flex", gap: "8px" }}>
        <div style={{ minWidth: "385px" }}>
          <label style={labelStyle}>
            Data Source<span style={{ color: "red" }}>*</span>
          </label>
          <Select
            isMulti
            value={getDataSourceAsArray().map((val) => ({
              value: val,
              label: val,
            }))}
            required={true}
            onChange={async (selectedOptions) => {
              const selectedValues = selectedOptions?.map((opt) => opt.value) || [];
              setLoader(true);
              
              const nifiSources = selectedValues.filter((source) =>
                savedQueries.some((q) => q.serviceName === source)
              );

              const fieldGroups = { ...newItem.fieldsBySource || {} };

              for (const source of nifiSources) {
                const matchedQuery = savedQueries.find(
                  (q) => q.serviceName === source
                );
                
                if (!matchedQuery) {
                  console.error(`No matching query found for source: ${source}`);
                  continue;
                }

                try {
                  const queryObject =
                    typeof matchedQuery.queryBuilder === "string"
                      ? JSON.parse(matchedQuery.queryBuilder)
                      : matchedQuery.queryBuilder;

                  const filters = {};

                  try {
                    const userResponse = await getData(getThemeURL);
                    if (
                      userResponse &&
                      Array.isArray(userResponse) &&
                      userResponse.length > 0
                    ) {
                      const user = userResponse[0];

                      // **UPDATED**: Apply dynamic filter logic for data source selection
                      if (user.role_name === "SUPER ADMIN") {
                        // For Super Admin, use selectedBankIds if available
                        if (selectedBankIds && selectedBankIds.length > 0) {
                          // **NEW**: Use dynamic filter field determination
                          const { bankFilterField } = determineFilterFields(queryObject);
                          
                          if (bankFilterField) {
                            filters[bankFilterField] = {
                              operator: "IN",
                              value: selectedBankIds.map(Number)
                            };
                            console.log("Applied dynamic bank filter for Super Admin:", filters[bankFilterField]);
                          }
                        }
                      } else {
                        // For non-Super Admin users, use their specific IDs with dynamic field determination
                        const { bankFilterField } = determineFilterFields(queryObject);
                        
                        if (user.bank_id && bankFilterField) {
                          filters[bankFilterField] = {
                            operator: "IN",
                            value: [user.bank_id] // Convert single ID to array format
                          };
                          console.log("Applied dynamic bank filter for regular user:", filters[bankFilterField]);
                        }
                      }
                    }
                  } catch (error) {
                    console.error(
                      "Error fetching user profile for filters:",
                      error
                    );
                  }

                  const enhancedQuery = {
                    ...queryObject,
                    filters: {
                      ...queryObject.filters,
                      ...filters,
                    },
                  };

                  const response = await fetch("/nifi/fetchQuery", {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify(enhancedQuery),
                  });

                  if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                  }

                  const result = await response.json();

                  const cleanColumns = result.columns?.map((col) =>
                    col.includes(".") ? col.split(".").pop() : col
                  ) || [];

                  const allFields = cleanColumns.map((field, index) => ({
                    key: field,
                    label: field
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase()),
                    selected: true,
                    order: index + 1,
                    hidden: field === "bank_id" || field === "branch_id",
                  }));

                  fieldGroups[source] = allFields;

                  setDataSourcesForMap((prev) => ({
                    ...prev,
                    [source]: {
                      sampleData: result.data || [],
                      originalData: result.data || [],
                      columns: cleanColumns,
                    },
                  }));
                } catch (error) {
                  console.error("Failed to fetch NiFi data for map:", error);
                } finally {
                  setLoader(false);
                }
              }

              // Clean up icons for removed sources
              const removedSources = getDataSourceAsArray().filter(
                source => !selectedValues.includes(source)
              );
              
              removedSources.forEach(source => {
                handleResetIcon(source);
              });

              Object.keys(fieldGroups).forEach((source) => {
                if (!selectedValues.includes(source)) {
                  delete fieldGroups[source];
                }
              });

              setNewItem(prevItem => ({
                ...prevItem,
                dataSource: selectedValues,
                fieldsBySource: fieldGroups,
                // Preserve the dates when changing data source
                fromDate: "",
                toDate: "",
                dateFilterApplied: false,
                dateFieldsChanged: false,
              }));
            }}
            options={[
              ...savedQueries.map((query) => ({
                value: query.serviceName,
                label: `${query.serviceName}`,
              })),
            ]}
            placeholder="Select data sources"
            styles={{
              container: (base) => ({
                ...base,
                marginBottom: "16px",
              }),
            }}
          />
          {(!newItem.dataSource || getDataSourceAsArray().length === 0) && (
            <span
              style={{
                color: "#ef4444",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              Data source is required
            </span>
          )}
        </div>

        <div style={{ flex: 1 }}>
          <div>
            <label style={labelStyle}>Initial Zoom Level</label>
            <input
              type="range"
              min="1"
              max="18"
              value={newItem.zoom || 13}
              onChange={(e) =>
                setNewItem(prevItem => ({
                  ...prevItem,
                  zoom: parseInt(e.target.value),
                }))
              }
            />
            <span style={{ marginLeft: "8px" }}>
              {newItem.zoom || 13}
            </span>
          </div>
        </div>
      </div>

      {getDataSourceAsArray().map((sourceKey) => (
        <div key={sourceKey} style={{ marginBottom: "24px" }}>
          <h4 style={{ fontWeight: "600", margin: "12px 0" }}>
            {sourceKey}
          </h4>

          <div style={{ marginBottom: "16px" }}>
            <div
              style={{
                display: "flex",
                width: "57%",
                justifyContent: "space-between",
              }}
            >
              <div style={labelStyle}>Marker Icon</div>
              <div style={labelStyle}>Marker Size</div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px solid #e5e7eb",
                  borderRadius: "8px",
                  backgroundColor: "#f9fafb",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <img
                  src={getIconSource(sourceKey)}
                  alt={hasCustomIcon(sourceKey) ? "Custom icon" : "Default icon"}
                  style={{
                    width: "32px",
                    height: "32px",
                    objectFit: "contain",
                  }}
                  onError={(e) => {
                    console.error("Image load error for:", sourceKey);
                    console.error("Failed URL:", e.target.src);
                    e.target.src = getDefaultIcon();
                  }}
                  onLoad={() => {
                    console.log("Image loaded successfully for:", sourceKey);
                  }}
                />
                {hasCustomIcon(sourceKey) && (
                  <div
                    style={{
                      position: "absolute",
                      top: "2px",
                      right: "2px",
                      width: "8px",
                      height: "8px",
                      backgroundColor: "#10b981",
                      borderRadius: "50%",
                      border: "1px solid white",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    }}
                    title="Custom icon uploaded"
                  />
                )}
              </div>

              <label
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#1a7a73",
                  color: "white",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "background-color 0.2s",
                  border: "none",
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = "#1a7a73"}
                onMouseOut={(e) => e.target.style.backgroundColor = "#1a7a73"}
              >
                <Upload size={16} />
                {"Change Icon"}
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                  style={{ display: "none" }}
                  onChange={async (e) => {
                    if (e.target.files && e.target.files[0]) {
                      const file = e.target.files[0];
                      
                      if (file.size > 5 * 1024 * 1024) {
                        alert('File size must be less than 5MB');
                        return;
                      }
                      
                      if (!file.type.startsWith('image/')) {
                        alert('Please select a valid image file');
                        return;
                      }
                      
                      await handleIconUploadWithAttachment(sourceKey, file);
                    }
                  }}
                />
              </label>

              {/* {hasCustomIcon(sourceKey) && (
                <button
                  onClick={() => handleResetIcon(sourceKey)}
                  style={{
                    padding: "8px",
                    backgroundColor: "#ef4444",
                    color: "white",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    transition: "background-color 0.2s",
                    border: "none",
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = "#dc2626"}
                  onMouseOut={(e) => e.target.style.backgroundColor = "#ef4444"}
                  title="Reset to default icon"
                >
                  <X size={16} />
                </button>
              )} */}

              <Select
                options={[
                  { value: [24, 24], label: "Small (24x24)" },
                  { value: [32, 32], label: "Medium (32x32)" },
                  { value: [48, 48], label: "Large (48x48)" },
                ]}
                value={{
                  value: iconSizes?.[sourceKey] || [32, 32],
                  label: (() => {
                    const size = iconSizes?.[sourceKey] || [32, 32];
                    if (size[0] === 24 && size[1] === 24) return "Small (24x24)";
                    if (size[0] === 32 && size[1] === 32) return "Medium (32x32)";
                    if (size[0] === 48 && size[1] === 48) return "Large (48x48)";
                    return `Custom (${size[0]}x${size[1]})`;
                  })(),
                }}
                onChange={(selected) => {
                  setIconSizes((prev) => ({
                    ...prev,
                    [sourceKey]: selected.value,
                  }));
                }}
                styles={{
                  container: (base) => ({
                    ...base,
                    width: "150px",
                  }),
                }}
              />
            </div>
          </div>

          {/* Field configuration */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "30px 1fr 1fr 50px",
              gap: "8px",
              padding: "8px",
              borderBottom: "1px solid #e5e7eb",
              fontWeight: "600",
              fontSize: "12px",
              color: "#64748b",
            }}
          >
            <span></span>
            <span style={{ marginLeft: "7px" }}>Labels</span>
            <span style={{ marginLeft: "-2px" }}>Fields</span>
            <span style={{ marginLeft: "-10px" }}>Order</span>
          </div>

          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "0 0 8px 8px",
              maxHeight: "200px",
              overflowY: "auto",
            }}
          >
            {newItem.fieldsBySource?.[sourceKey]
              ?.filter((field) => !field.hidden)
              ?.sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((field, idx) => {
                const originalIndex = newItem.fieldsBySource[
                  sourceKey
                ].findIndex((f) => f.key === field.key);

                return (
                  <div
                    key={field.key}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "30px 1fr 1fr 50px",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px",
                      backgroundColor:
                        idx % 2 === 0 ? "#f9fafb" : "white",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={field.selected !== false}
                      onChange={(e) => {
                        const updated = { ...newItem.fieldsBySource };
                        updated[sourceKey][originalIndex].selected =
                          e.target.checked;
                        setNewItem(prevItem => ({
                          ...prevItem,
                          fieldsBySource: updated,
                        }));
                      }}
                    />
                    <input
                      type="text"
                      value={field.label || ""}
                      onChange={(e) => {
                        const updated = { ...newItem.fieldsBySource };
                        updated[sourceKey][originalIndex].label =
                          e.target.value;
                        setNewItem(prevItem => ({
                          ...prevItem,
                          fieldsBySource: updated,
                        }));
                      }}
                      style={{
                        padding: "6px 8px",
                        borderRadius: "4px",
                        border: "1px solid #d1d5db",
                        fontSize: "12px",
                        width: "90%",
                      }}
                    />
                    <input
                      type="text"
                      value={field.key || ""}
                      disabled
                      style={{
                        padding: "6px 8px",
                        borderRadius: "4px",
                        border: "1px solid #d1d5db",
                        fontSize: "12px",
                        backgroundColor: "#f3f4f6",
                        color: "#6b7280",
                        cursor: "not-allowed",
                        width: "90%",
                      }}
                    />
                    <select
                      value={field.order || idx + 1}
                      onChange={(e) => {
                        const updated = { ...newItem.fieldsBySource };
                        updated[sourceKey][originalIndex].order =
                          parseInt(e.target.value);
                        setNewItem(prevItem => ({
                          ...prevItem,
                          fieldsBySource: updated,
                        }));
                      }}
                      style={{
                        padding: "4px",
                        borderRadius: "4px",
                        border: "1px solid #d1d5db",
                        fontSize: "12px",
                      }}
                    >
                      {getFieldOrderOptions(sourceKey).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
          </div>
        </div>
      ))}

      {/* Data filtering based on date range section - UPDATED WITH CONSISTENT LAYOUT */}
<div style={{ marginBottom: "24px" }}>
  <label
    style={{
      display: "block",
      marginBottom: "16px",
      fontSize: "16px",
      fontWeight: "bold",
      color: "#374151",
    }}
  >
    Data filtering based on date range
  </label>
  
  <div
    style={{
      display: "flex",
      gap: "16px",
      alignItems: "flex-start", // Changed from "flex-end" to "flex-start"
      flexWrap: "wrap",
    }}
  >
    {/* Date Field Selector Dropdown */}
    <div style={{ flex: 1, minWidth: "180px" }}>
      <label
        style={{
          display: "block",
          marginBottom: "8px",
          fontSize: "14px",
          fontWeight: "bold",
          color: "#374151",
        }}
      >
        Date Field
      </label>
      <select
        value={newItem.selectedDateField || ""}
        onChange={(e) => {
          setNewItem({
            ...newItem,
            selectedDateField: e.target.value,
            dateFilterApplied: false,
            dateFieldsChanged: true,
            fromDate: "", // Clear fromDate
            toDate: "", // Clear toDate
            mapDateValidationError: null,
            mapToDateValidationError: null,
          });
          setNoDataForDateRange(false);
        }}
        style={{
          width: "100%",
          padding: "10px 12px",
          borderRadius: "6px",
          border: "1px solid #d1d5db",
          fontSize: "14px",
          height: "40px",
          boxSizing: "border-box",
          backgroundColor: "white",
        }}
      >
        <option value="">Select date field</option>
        {dateFields.map((field) => (
          <option key={field.key} value={field.key}>
            {field.label}
          </option>
        ))}
      </select>

      {/* Reserved space for consistency */}
      <div
        style={{
          height: "32px", // Fixed height to prevent layout shifts
          display: "flex",
          alignItems: "flex-start",
          paddingTop: "4px",
        }}
      >
        {/* Empty space for consistency */}
      </div>
    </div>

    {/* From Date */}
    <div style={{ flex: 1, minWidth: "150px" }}>
      <label
        style={{
          display: "block",
          marginBottom: "8px",
          fontSize: "14px",
          fontWeight: "bold",
          color: "#374151",
        }}
      >
        From Date
      </label>
      <input
        type="date"
        value={newItem.fromDate || ""}
        disabled={!newItem.selectedDateField}
        onChange={(e) => {
          const selectedDate = e.target.value;
          const today = new Date().toISOString().split("T")[0];

          if (selectedDate > today) {
            const updatedItem = {
              ...newItem,
              fromDate: today,
              dateFieldsChanged: true,
              mapDateValidationError:
                "Future dates are not allowed. Date has been set to today.",
            };

            if (newItem.toDate && today > newItem.toDate) {
              updatedItem.toDate = "";
              updatedItem.mapToDateValidationError =
                "To Date cleared because From Date was adjusted to today.";
            }

            setNewItem(updatedItem);

            setTimeout(() => {
              setNewItem((prev) => ({
                ...prev,
                mapDateValidationError: null,
              }));
            }, 3000);

            return;
          }

          const updatedItem = {
            ...newItem,
            fromDate: selectedDate,
            mapDateValidationError: null,
            dateFilterApplied: false,
            dateFieldsChanged: true,
          };

          if (newItem.toDate && newItem.toDate < selectedDate) {
            updatedItem.toDate = "";
            updatedItem.mapToDateValidationError =
              "To Date cleared because it was earlier than From Date.";
          }

          setNewItem(updatedItem);
          setNoDataForDateRange(false);
        }}
        max={new Date().toISOString().split("T")[0]}
        style={{
          width: "100%",
          padding: "10px 12px",
          borderRadius: "6px",
          border: newItem.mapDateValidationError
            ? "1px solid #ef4444"
            : "1px solid #d1d5db",
          fontSize: "14px",
          height: "40px",
          boxSizing: "border-box",
          backgroundColor: !newItem.selectedDateField ? "#f3f4f6" : "white", // Add visual indication when disabled
          cursor: !newItem.selectedDateField ? "not-allowed" : "pointer",
        }}
        onFocus={(e) => (e.target.style.borderColor = "#1a7a73")}
        onBlur={(e) =>
          (e.target.style.borderColor = newItem.mapDateValidationError
            ? "#ef4444"
            : "#d1d5db")
        }
      />

      {/* Reserved space for error message */}
      <div
        style={{
          height: "32px",
          display: "flex",
          alignItems: "flex-start",
          paddingTop: "4px",
        }}
      >
        {newItem.mapDateValidationError && (
          <span
            style={{
              color: "#ef4444",
              fontSize: "12px",
              lineHeight: "16px",
            }}
          >
            {newItem.mapDateValidationError}
          </span>
        )}
      </div>
    </div>

    {/* To Date */}
    <div style={{ flex: 1, minWidth: "150px" }}>
      <label
        style={{
          display: "block",
          marginBottom: "8px",
          fontSize: "14px",
          fontWeight: "bold",
          color: "#374151",
        }}
      >
        To Date
      </label>
      <input
        type="date"
        value={newItem.toDate || ""}
        disabled={!newItem.selectedDateField}
        onChange={(e) => {
          const selectedDate = e.target.value;
          const today = new Date().toISOString().split("T")[0];

          if (selectedDate > today) {
            setNewItem(prevItem => ({
              ...prevItem,
              toDate: today,
              dateFieldsChanged: true,
              mapToDateValidationError:
                "Future dates are not allowed. Date has been set to today.",
            }));

            setTimeout(() => {
              setNewItem((prev) => ({
                ...prev,
                mapToDateValidationError: null,
              }));
            }, 3000);

            return;
          }

          if (newItem.fromDate && selectedDate < newItem.fromDate) {
            setNewItem(prevItem => ({
              ...prevItem,
              toDate: selectedDate,
              dateFieldsChanged: true,
              mapToDateValidationError:
                "To Date cannot be earlier than From Date.",
            }));

            return;
          }

          setNewItem(prevItem => ({
            ...prevItem,
            toDate: selectedDate,
            dateFieldsChanged: true,
            dateFilterApplied: false,
            mapToDateValidationError: null,
            mapDateValidationError: null,
          }));
          setNoDataForDateRange(false);
        }}
        max={new Date().toISOString().split("T")[0]}
        // Remove min as discussed previously
        // min={newItem.fromDate || ""}
        style={{
          width: "100%",
          padding: "10px 12px",
          borderRadius: "6px",
          border: newItem.mapToDateValidationError
            ? "1px solid #ef4444"
            : "1px solid #d1d5db",
          fontSize: "14px",
          height: "40px",
          boxSizing: "border-box",
          backgroundColor: !newItem.selectedDateField ? "#f3f4f6" : "white", // Add visual indication when disabled
          cursor: !newItem.selectedDateField ? "not-allowed" : "pointer",
        }}
        onFocus={(e) => (e.target.style.borderColor = "#1a7a73")}
        onBlur={(e) =>
          (e.target.style.borderColor = newItem.mapToDateValidationError
            ? "#ef4444"
            : "#d1d5db")
        }
      />

      {/* Reserved space for error message */}
      <div
        style={{
          height: "32px",
          display: "flex",
          alignItems: "flex-start",
          paddingTop: "4px",
        }}
      >
        {newItem.mapToDateValidationError && (
          <span
            style={{
              color: "#ef4444",
              fontSize: "12px",
              lineHeight: "16px",
            }}
          >
            {newItem.mapToDateValidationError}
          </span>
        )}
      </div>
    </div>

    {/* Update Filter Button - Positioned to align with input fields */}
<div style={{ 
  display: "flex", 
  flexDirection: "column", 
  justifyContent: "flex-start" 
}}>
  {/* Invisible label for alignment */}
  <div style={{ height: "24px", marginBottom: "8px" }}></div>
  
  <button
    onClick={handleMapDateRefresh}
    disabled={
      !newItem.dataSource || 
      !newItem.fromDate || 
      !newItem.toDate || 
      !newItem.selectedDateField ||
      newItem.mapDateValidationError ||      // Add validation error check
      newItem.mapToDateValidationError       // Add validation error check
    }
    style={{
      padding: "12px 20px",
      borderRadius: "6px",
      border: "1px solid #d1d5db",
      backgroundColor: (
        !newItem.dataSource || 
        !newItem.fromDate || 
        !newItem.toDate || 
        !newItem.selectedDateField ||
        newItem.mapDateValidationError ||      // Add validation error check
        newItem.mapToDateValidationError       // Add validation error check
      ) 
        ? "#f3f4f6" 
        : "#1a7a73",
      color: (
        !newItem.dataSource || 
        !newItem.fromDate || 
        !newItem.toDate || 
        !newItem.selectedDateField ||
        newItem.mapDateValidationError ||      // Add validation error check
        newItem.mapToDateValidationError       // Add validation error check
      ) 
        ? "#9ca3af" 
        : "white",
      cursor: (
        !newItem.dataSource || 
        !newItem.fromDate || 
        !newItem.toDate || 
        !newItem.selectedDateField ||
        newItem.mapDateValidationError ||      // Add validation error check
        newItem.mapToDateValidationError       // Add validation error check
      ) 
        ? "not-allowed" 
        : "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "14px",
      fontWeight: "500",
      height: "40px",
      transition: "all 0.2s ease",
      whiteSpace: "nowrap",
    }}
    title={
      newItem.mapDateValidationError || newItem.mapToDateValidationError
        ? "Please fix date validation errors before applying filter"
        : "Apply date filter and refresh data"
    }
  >
    Update Filter
  </button>

  {/* Reserved space to match other fields */}
  <div style={{ height: "32px" }}></div>
</div>
  </div>

  {/* Warning message for unapplied date filters */}
  {(newItem.fromDate && newItem.toDate && newItem.selectedDateField) && 
   !newItem.dateFilterApplied && 
   newItem.dateFieldsChanged && !newItem.mapDateValidationError &&         // Only show when no validation errors
   !newItem.mapToDateValidationError && !noDataForDateRange &&(
    <div
      style={{
        marginTop: "12px",
        padding: "12px 16px",
        backgroundColor: "#fef3c7",
        border: "1px solid #f59e0b",
        borderRadius: "6px",
        color: "#92400e",
        fontSize: "14px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        width: "100%",
      }}
    >
      <AlertTriangle size={16} />
      Click "Update Filter" to apply the date range before saving the widget.
    </div>
  )}

  {/* Date Range Validation Error */}
  {newItem.fromDate && !newItem.toDate && (
    <div
      style={{
        marginTop: "12px",
        padding: "12px 16px",
        backgroundColor: "#fef2f2",
        border: "1px solid #fecaca",
        borderRadius: "6px",
        color: "#dc2626",
        fontSize: "14px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <AlertTriangle size={16} />
      Please select both From Date and To Date to create a valid date range for the map.
    </div>
  )}

  {/* Date Field Selection Validation Error */}
  {newItem.fromDate && newItem.toDate && !newItem.selectedDateField && (
    <div
      style={{
        marginTop: "12px",
        padding: "12px 16px",
        backgroundColor: "#fef2f2",
        border: "1px solid #fecaca",
        borderRadius: "6px",
        color: "#dc2626",
        fontSize: "14px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <AlertTriangle size={16} />
      Please select a date field to filter by.
    </div>
  )}
</div>
{noDataForDateRange && (
  <div
    style={{
      marginTop: "12px",
      padding: "12px 16px",
      backgroundColor: "#f0f9ff",
      border: "1px solid #1a7a73",
      borderRadius: "6px",
      color: "#0f4a45",
      fontSize: "14px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    }}
  >
    <AlertTriangle size={16} />
    No data found for the selected date range.
  </div>
)}
    </>
  );
};

export default MapEdit;
